/**
 * Full-dashboard screenshot.
 *
 * Captures the whole `.dashboard-content` scroll container — every widget,
 * including the ones below the fold — as one PNG, headed by the dashboard's
 * title and data-period line so the image stands on its own. Separate from
 * widgetExport.ts, which captures a single KPI card and re-renders it
 * off-screen at a fixed width; here the point is the real dashboard exactly
 * as laid out on this screen.
 */

import html2canvas from "html2canvas";

// Browsers cap canvas area (Safari is the tightest at roughly 16.7M pixels);
// past it toDataURL silently returns a blank image. A tall dashboard at
// scale 2 clears that easily, so the scale is reduced to fit rather than
// letting the download come out empty.
const MAX_CANVAS_PIXELS = 16_000_000;

// 1.5, not 2, from measuring a 14-widget dashboard end to end:
//
//   scale 2.00   render 1345ms  encode 862ms  total 2207ms   2.07 MB
//   scale 1.50   render 1380ms  encode 405ms  total 1785ms   1.42 MB
//   scale 1.25   render 1399ms  encode 284ms  total 1683ms   1.11 MB
//
// Render time is flat at every scale -- only PNG encoding grows with the pixel
// count -- so the whole cost of a sharper image lands in the encode step and
// the file size. 1.5 is still sharper than a standard screen; below ~1.25 there
// is almost nothing left to win (1683ms vs 1649ms at scale 1), so going lower
// only costs legibility.
const PREFERRED_SCALE = 1.5;

const scaleFor = (width: number, height: number): number => {
  const area = width * height;
  if (area <= 0) return 1;
  const maxScale = Math.sqrt(MAX_CANVAS_PIXELS / area);
  // Never upscale past the preferred scale, and never below 1 — a blurry
  // capture is still readable, a blank one isn't.
  return Math.max(Math.min(PREFERRED_SCALE, maxScale), 1);
};

const fileName = (dashboardName?: string): string => {
  const slug =
    (dashboardName ?? "dashboard")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "dashboard";
  return `${slug}-${new Date().toISOString().slice(0, 10)}.png`;
};

/**
 * The container is a fixed-height scroll box (`height: calc(100vh - 170px)`
 * in generic.scss), so html2canvas would otherwise capture only the visible
 * slice. These overrides let it lay out at full content height for the
 * duration of the capture; the previous inline values are returned so the
 * caller can put them back even if the capture throws.
 */
const expandForCapture = (el: HTMLElement) => {
  const previous = {
    height: el.style.height,
    maxHeight: el.style.maxHeight,
    overflow: el.style.overflow,
  };
  el.style.height = "auto";
  el.style.maxHeight = "none";
  el.style.overflow = "visible";
  return () => {
    el.style.height = previous.height;
    el.style.maxHeight = previous.maxHeight;
    el.style.overflow = previous.overflow;
  };
};

/**
 * Mounts a copy of the page's title and data-period line off screen purely to
 * measure them: laying them out at the capture width is the only way to know
 * how many lines the context sentence wraps to, and so how much taller the
 * canvas must be. The markup itself is injected into html2canvas's clone.
 *
 * Same technique widgetExport.ts uses for KPI cards (`left:-100000px`).
 * Earlier this inserted the clone straight into the scroll container, which
 * worked but left a visible duplicate header on screen for the whole capture.
 *
 * Mounted at the capture width so the context line wraps exactly as it will
 * in the PNG, and anything marked `data-html2canvas-ignore` (the Download
 * button) is skipped by html2canvas.
 */
const mountHeaderHost = (
  nodes: readonly (HTMLElement | null | undefined)[],
  width: number,
): HTMLElement | null => {
  const present = nodes.filter((node): node is HTMLElement => !!node);
  if (present.length === 0 || width <= 0) return null;

  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;top:0;left:-100000px;width:${width}px;` +
    `background:#ffffff;z-index:-1;pointer-events:none;padding:4px 2px 12px;`;
  present.forEach((node) => host.appendChild(node.cloneNode(true)));

  // html2canvas applies `data-html2canvas-ignore` while building its own
  // clone, so anything injected later in `onclone` slips past that filter --
  // which is how the Download button ("Capturing...") ended up in the image.
  // Honour the marker here instead, which also frees the space it occupied.
  host
    .querySelectorAll("[data-html2canvas-ignore]")
    .forEach((node) => node.remove());

  document.body.appendChild(host);
  return host;
};

type DashboardPngOptions = {
  /** Used to build the download filename. */
  dashboardName?: string;
  /**
   * Page elements to clone in above the widgets, in order — typically the
   * dashboard title and the data-period line. Nulls are skipped, so callers
   * can pass refs without guarding each one.
   */
  headerNodes?: readonly (HTMLElement | null | undefined)[];
};

export const downloadDashboardPng = async (
  el: HTMLElement,
  { dashboardName, headerNodes = [] }: DashboardPngOptions = {},
): Promise<void> => {
  const restore = expandForCapture(el);
  let host: HTMLElement | null = null;
  try {
    // One frame so the expanded layout is committed before measuring —
    // reading scrollHeight straight after the style change can still return
    // the clipped height.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

    const width = el.scrollWidth;
    const height = el.scrollHeight;

    // The host is mounted only to be *measured*: laying it out at the final
    // width is what tells us how many lines the context sentence wraps to, and
    // therefore how much taller the canvas has to be. It is never rasterised.
    //
    // Rasterising it separately is what the earlier version did, and it cost
    // ~5.6s of an 8.4s capture: html2canvas clones the whole document however
    // small the element handed to it, so a 60px strip paid the full price of
    // cloning every widget on the page. The header is now injected into the
    // clone of the single pass below instead.
    host = mountHeaderHost(headerNodes, width);
    if (host) {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }
    const headerHeight = host?.offsetHeight ?? 0;
    const headerHtml = host?.innerHTML ?? "";
    const headerPadding = host?.style.padding ?? "";

    const scale = scaleFor(width, height + headerHeight);

    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale,
      logging: false,
      useCORS: true,
      width,
      height: height + headerHeight,
      // Runs against html2canvas's private clone, so the header appears in the
      // image without ever existing on the visible page.
      onclone: (_doc: Document, cloned: HTMLElement) => {
        if (!headerHtml) return;
        const banner = cloned.ownerDocument.createElement("div");
        banner.style.padding = headerPadding;
        banner.innerHTML = headerHtml;
        cloned.insertBefore(banner, cloned.firstChild);
      },
      // scrollX/scrollY pin the capture to the top of the element; without
      // them html2canvas offsets it by the page's current scroll position and
      // the top of the dashboard comes out cropped.
      scrollX: 0,
      scrollY: 0,
      // The *real* viewport, not the element's size. html2canvas clones the
      // whole page and lays it out in a window of these dimensions, so passing
      // the container's width made the clone re-flow as if the browser were
      // that narrow: the filters sidebar still claimed its 230px, shrinking
      // .dashboard-content, and `.card { max-width: 100% }` then clamped every
      // full-width widget to ~77%. Narrower widgets still fitted, which is why
      // only the full-width charts came out short.
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });

    // toBlob rather than toDataURL: encoding a dashboard-sized canvas to a
    // base64 data URL is a long synchronous block that also builds a string
    // a third larger than the image. toBlob hands back binary asynchronously.
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) throw new Error("Could not encode the dashboard image.");

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName(dashboardName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // The browser needs the URL to still resolve when the click is handled;
    // freeing it on the next tick avoids leaking a dashboard-sized blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } finally {
    // In a finally so a failed capture can't leave the dashboard stuck at
    // full height with its scrollbar gone, or the off-screen host attached.
    host?.remove();
    restore();
  }
};
